import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import { getClassroomStatus } from '../../api/classroom';
import { getApiErrorInfo } from '../../utils/apiError';

/**
 * Classroom hub — entry point for live online classes (Zoom-first).
 * role: ADMIN | MANAGER | COACH | STUDENT | PARENT
 */
export default function ClassroomHub({ role = 'ADMIN' }) {
  const { t } = useTranslation();
  const base = `/${String(role).toLowerCase()}/classroom`;
  const isStaff = role === 'ADMIN' || role === 'MANAGER' || role === 'COACH';
  const isAdmin = role === 'ADMIN';
  const [zoomConnected, setZoomConnected] = useState(null);
  const [defaultProvider, setDefaultProvider] = useState(null);
  const [statusError, setStatusError] = useState('');

  const loadStatus = async () => {
    setStatusError('');
    try {
      const status = await getClassroomStatus();
      setZoomConnected(!!status?.zoomConnected);
      setDefaultProvider(status?.defaultProvider || 'ZOOM');
    } catch (err) {
      setZoomConnected(null);
      setDefaultProvider(null);
      setStatusError(getApiErrorInfo(err, t('classroom.hub.statusLoadFailed')).message);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const externalMode = String(defaultProvider || '').toUpperCase() === 'EXTERNAL_LINK';
  const classroomReady = zoomConnected || externalMode;

  const cards = useMemo(() => {
    const items = [
      {
        to: `${base}/live`,
        title: t('classroom.hub.liveTitle'),
        desc: isStaff ? t('classroom.hub.liveDescStaff') : t('classroom.hub.liveDescLearner'),
        tone: 'from-sky-500 to-indigo-600',
      },
    ];
    if (isAdmin) {
      items.push(
        {
          to: `${base}/zoom`,
          title: t('classroom.hub.zoomTitle'),
          desc: zoomConnected
            ? t('classroom.hub.zoomDescConnected')
            : t('classroom.hub.zoomDesc'),
          tone: 'from-blue-600 to-blue-800',
        },
        {
          to: `${base}/settings`,
          title: t('classroom.hub.settingsTitle'),
          desc: t('classroom.hub.settingsDesc'),
          tone: 'from-slate-600 to-slate-800',
        },
      );
    }
    return items;
  }, [base, isAdmin, isStaff, t, zoomConnected]);

  return (
    <div className="page-shell-form max-w-4xl">
      <PageHeaderSection
        eyebrow={t('nav.classroom')}
        title={t('classroom.hub.title')}
        subtitle={
          isStaff ? t('classroom.hub.subtitleStaff') : t('classroom.hub.subtitleLearner')
        }
        showBack={false}
      />

      {statusError && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          <p>{statusError}</p>
          <Button type="button" variant="page-secondary" className="mt-2" onClick={loadStatus}>
            {t('classroom.live.refresh')}
          </Button>
        </div>
      )}

      <div
        className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
          statusError
            ? 'border-slate-200 bg-slate-50 text-slate-700'
            : zoomConnected == null
              ? 'border-slate-200 bg-slate-50 text-slate-700'
              : classroomReady
                ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                : 'border-amber-200 bg-amber-50 text-amber-950'
        }`}
        role="status"
      >
        {statusError
          ? t('classroom.hub.zoomStatusUnknown')
          : zoomConnected == null
            ? t('classroom.hub.zoomFirstBanner')
            : classroomReady
              ? (externalMode && !zoomConnected
                ? t('classroom.hub.externalModeBanner')
                : t('classroom.hub.zoomConnectedBanner'))
              : (
                <>
                  {t('classroom.hub.zoomNotConnectedBanner')}
                  {isAdmin && (
                    <>
                      {' '}
                      <Link to={`${base}/zoom`} className="font-semibold underline hover:no-underline">
                        {t('classroom.live.connectZoomCta')}
                      </Link>
                    </>
                  )}
                </>
              )}
      </div>

      <div className={`grid gap-4 sm:grid-cols-2 ${isAdmin ? 'lg:grid-cols-3' : ''}`}>
        {cards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-sky-300 hover:shadow-md"
          >
            <div className={`h-2 bg-gradient-to-r ${card.tone}`} />
            <div className="p-5">
              <h2 className="text-lg font-semibold text-slate-900 group-hover:text-sky-800">
                {card.title}
              </h2>
              <p className="mt-2 text-sm text-slate-600">{card.desc}</p>
              <span className="mt-4 inline-flex text-sm font-semibold text-sky-700">
                {t('classroom.hub.open')} →
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
